'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Ice: Does Your Ice Maker or Tray Add Plastic? (2026)"
      author="MicroPlastics Research Desk"
      date="July 17, 2026"
      readTime="8 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Ice cubes in a glass of water on a bright summer counter"
    >
      <BlogLastUpdated iso="2026-07-17" formatted="July 17, 2026" />

      <BlogTLDR>
        <strong>Ice is only as clean as the water you froze,</strong> and{' '}
        <strong>freezing removes nothing</strong>, every microplastic in your
        tap or bottled water is still there in the cube. On top of that, the{' '}
        <strong>tray or ice maker can add more</strong>: flexible plastic trays,
        ice-maker bins, augers, and tubing all flex, scrape, and shed with use,
        and the plastic in an automatic ice maker sits wet and cold for months.
        Cheap bagged ice is a wildcard, sometimes filtered, sometimes not. The
        lowest-microplastic ice is <strong>filtered water frozen in a silicone
        or stainless-steel tray</strong>, stored in a glass or steel container.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Freezing does not remove microplastics, ice carries whatever its source water had, so filter the water first.</>,
          <>Flexible plastic ice trays flex and abrade every time you twist them, releasing particles into the cubes.</>,
          <>Automatic ice makers add plastic bins, augers, and tubing that sit wet and cold long-term, a shedding surface most people never think about.</>,
          <>Bagged/commercial ice quality varies, some is filtered RO water, some is not, and the bag itself is plastic.</>,
          <>Cleanest home ice: filtered water in a silicone or steel tray, stored in glass or steel.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_ice" />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The thing almost everyone gets wrong: freezing doesn&apos;t purify
      </h2>
      <p>
        There&apos;s a quiet assumption that ice is somehow &ldquo;clean&rdquo;
        water, cold, clear, solid. It isn&apos;t. Freezing is a physical state
        change; it does nothing to remove microplastics, and unlike boiling
        (which some studies suggest can help precipitate certain particles out
        of hard water), it has no purifying effect at all. If your tap water has
        microplastics, your ice has the same microplastics. If you freeze bottled
        water, which averages far more particles than tap, your ice inherits
        that too.
      </p>
      <p>
        So the first lever is the same one that matters for every drink you make:{' '}
        <strong>the source water</strong>. Filter it before it ever becomes ice.
        Our{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          water filters compared
        </Link>{' '}
        guide covers which filters actually remove micro/nanoplastics.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the ice itself touches
      </h2>
      <p>
        The second lever is everything the water touches on its way to becoming a
        cube, and this is where the &ldquo;ice adds plastic&rdquo; part comes in:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Flexible plastic trays.</strong> The reason they&apos;re convenient, they twist to pop cubes out, is exactly the problem. Flexing stresses and abrades the plastic every cycle, and scratched trays shed more over time.</li>
        <li><strong>Automatic ice makers.</strong> Fridge and countertop ice makers move water through plastic reservoirs, tubing, augers, and storage bins. That plastic stays cold and wet for months, and mechanical parts (augers, ejectors) grind against the ice.</li>
        <li><strong>The storage.</strong> Ice left sitting in a plastic bin picks up more contact time; a glass or steel container avoids it.</li>
        <li><strong>Bagged ice.</strong> Convenience-store and party ice is frozen from someone else&apos;s water (quality varies), then packed in a plastic bag. It&apos;s a wildcard, not automatically better than home ice.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Ice methods ranked for microplastics
      </h2>

      <BlogComparisonTable
        caption="Home and store ice, lowest to highest plastic exposure"
        columns={[
          { key: 'method', label: 'Method' },
          { key: 'water', label: 'Source water' },
          { key: 'contact', label: 'Plastic contact' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { method: 'Filtered water, stainless-steel tray', water: 'Filtered', contact: 'None', verdict: 'Cleanest' },
          { method: 'Filtered water, silicone tray', water: 'Filtered', contact: 'Low-leaching silicone', verdict: 'Excellent, easy to use' },
          { method: 'Filtered water, rigid plastic tray', water: 'Filtered', contact: 'Some (less if not twisted hard)', verdict: 'Good; upgrade the tray' },
          { method: 'Tap water, flexible plastic tray', water: 'Unfiltered', contact: 'Tray shedding + tap baseline', verdict: 'Two sources stacked; filter the water' },
          { method: 'Automatic ice maker (fridge/countertop)', water: 'Usually unfiltered unless plumbed to a filter', contact: 'Bins, augers, tubing', verdict: 'Convenient but plastic-heavy path' },
          { method: 'Bagged/commercial ice', water: 'Varies (sometimes RO)', contact: 'Bag + unknown equipment', verdict: 'Wildcard; can be clean or not' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How to make the lowest-microplastic ice
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Filter the water first,</strong> this is the biggest win, since the cube inherits it directly.</li>
        <li>Freeze in a <strong>stainless-steel or silicone tray</strong> instead of a flexible plastic one.</li>
        <li><strong>Replace scratched, cloudy plastic trays</strong>, wear increases shedding.</li>
        <li>If you use a fridge ice maker, <strong>plumb it through a filter</strong> and change the filter on schedule.</li>
        <li><strong>Store cubes in glass or steel,</strong> not a plastic bin, and don&apos;t let them sit for weeks absorbing freezer odors and contact.</li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>The material of your ice trays and storage bins (silicone, steel, or plastic) from a photo.</>,
          <>A 0&ndash;100 risk score for the trays, bottles, and bins your ice touches.</>,
          <>Whether your fridge or pitcher filter is certified to remove micro/nanoplastics.</>,
          <>A lower-plastic swap (steel/silicone tray, certified filter) in the same size.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan your trays, filter, and water before the next batch"
        sub="Ice inherits your water and your trays. Scan them with MicroPlastics to see which one drives the plastic, and the silicone, steel, or certified-filter swap that fixes it."
        buttonLabel="Scan my ice setup"
      />

      <p>
        Related:{' '}
        <Link href="/blog/microplastics-in-drinking-water-by-country" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">microplastics in tap water</Link>,{' '}
        <Link href="/blog/water-bottle-left-in-hot-car-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">water bottle left in a hot car</Link>, and{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">water filters compared</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Does freezing water remove microplastics?',
            answer:
              'No. Freezing is only a change of state and does not remove microplastics, ice carries whatever particles were in the source water. If you want cleaner ice, filter the water before freezing; the cube inherits the water directly.',
          },
          {
            question: 'Do plastic ice trays release microplastics?',
            answer:
              "They can. Flexible plastic trays flex and abrade every time you twist them to release cubes, and scratched or old trays shed more. Stainless-steel or silicone trays avoid or minimize this. Rigid plastic trays that aren't twisted hard are a middle ground.",
          },
          {
            question: 'Is ice from a fridge ice maker high in microplastics?',
            answer:
              'It can carry more than a simple tray because the water passes through plastic reservoirs, tubing, augers, and a storage bin that stay cold and wet for months. The biggest fixes are plumbing the ice maker through a certified filter and changing that filter on schedule.',
          },
          {
            question: 'What is the cleanest way to make ice at home?',
            answer:
              "Filter your water, freeze it in a stainless-steel or silicone tray rather than flexible plastic, and store the cubes in glass or steel. Filtering the source water is the single biggest step, since ice inherits the source water's microplastics unchanged.",
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy (bottled water ~240,000 particles/L)',
            publisher: 'Proceedings of the National Academy of Sciences (PNAS)',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'Yang Z, et al.',
            year: 2024,
            title: 'Drinking boiled tap water reduces human intake of nanoplastics and microplastics',
            publisher: 'Environmental Science & Technology Letters',
            url: 'https://pubs.acs.org/doi/10.1021/acs.estlett.4c00081',
          },
          {
            authors: 'Kosuth M, Mason SA, Wattenberg EV.',
            year: 2018,
            title: 'Anthropogenic contamination of tap water, beer, and sea salt',
            publisher: 'PLoS ONE',
            url: 'https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0194970',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
